/**
 * Extracts nodes that have searchParams from the blockchain data
 * @param {Object|string} jsonData - The JSON data or string to process
 * @returns {Array} - Array of objects with address and chain for nodes with searchParams
 */
export const extractSearchedNodes = (jsonData) => {
  try {
    // Parse the JSON if it's a string
    const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

    // Extract nodes that have searchParams
    const searchedNodes = [];

    // Handle the specific structure provided in the JSON data
    if (Array.isArray(data)) {
      // Get the first item (assuming it's the main data object)
      const mainData = data[0];
      if (
        mainData &&
        mainData.data &&
        mainData.data.nodes &&
        Array.isArray(mainData.data.nodes)
      ) {
        mainData.data.nodes.forEach((node) => {
          if (
            node.data &&
            node.data.searchParams &&
            node.data.searchParams.length > 0
          ) {
            // Try to extract address from various possible locations
            let address;

            // Method 1: Try to get from address._id
            if (node.data.address && node.data.address._id) {
              address = node.data.address._id;
            }

            // Method 2: Try to get from searchParams.filterAddress if Method 1 failed
            if (
              !address &&
              node.data.searchParams[0] &&
              node.data.searchParams[0].filterAddress
            ) {
              address = node.data.searchParams[0].filterAddress;
            }

            if (address) {
              const searchedNode = {
                address: address,
                chain: node.data.chain,
              };
              searchedNodes.push(searchedNode);
            }
          }
        });
      }
    }

    return searchedNodes;
  } catch (error) {
    console.error('Error extracting searched nodes:', error);
    return [];
  }
};

/**
 * Categorizes blockchain nodes based on specific label types with more detailed categories
 * @param {Object|string} jsonData - The JSON data or string to process
 * @returns {Object} - Object with categorized nodes in multiple specialized categories
 */
export const categorizeNodes = (jsonData) => {
  try {
    // Parse the JSON if it's a string
    const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

    // Define categories to look for
    const categories = {
      Contract: [],
      ContractCreator: [],
      CEX: [],
      DEX: [],
      Oracle: [],
      Bridge: [],
      Mixer: [],
      MixerUser: [],
      KYC: [],
    };

    // Process nodes based on the structure in the provided data
    let nodes = [];

    // Handle the specific structure provided in the JSON data
    if (Array.isArray(data)) {
      // Get the first item (assuming it's the main data object)
      const mainData = data[0];
      if (
        mainData &&
        mainData.data &&
        mainData.data.nodes &&
        Array.isArray(mainData.data.nodes)
      ) {
        nodes = mainData.data.nodes;
      }
    } else if (data.data && data.data.nodes && Array.isArray(data.data.nodes)) {
      nodes = data.data.nodes;
    }

    // Process all nodes
    nodes.forEach((node) => {
      if (node.data.address && node.data.address.labels) {
        // Extract address from address._id
        const address = node.data.address._id;
        if (!address) return;

        const chain = node.data.chain;

        // Check each label to categorize the node
        node.data.address.labels.forEach((label) => {
          // Contract category checks (excluding contract creators)
          if (
            (label.label === 'Contract' ||
              label.type === 'Contract Standard' ||
              label.label === 'ERC20') &&
            !label.label.includes('Creator')
          ) {
            categories.Contract.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // Contract Creator category
          if (
            label.label === 'Contract Creator' ||
            label.label.includes('Contract Creator')
          ) {
            categories.ContractCreator.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // CEX (Centralized Exchange) category checks
          if (label.type === 'CEX') {
            categories.CEX.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // DEX (Decentralized Exchange) category checks
          if (label.type === 'DEX') {
            categories.DEX.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // Oracle category checks
          if (
            label.label.includes('Oracle') ||
            (label.type && label.type.includes('Oracle'))
          ) {
            categories.Oracle.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // Bridge category checks
          if (
            label.label.includes('Bridge') ||
            (label.type && label.type.includes('Bridge'))
          ) {
            categories.Bridge.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // Mixer category checks (for mixer services)
          if (label.label === 'Mixer' || label.type === 'Mixer') {
            categories.Mixer.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // Mixer User category checks
          if (
            label.type === 'Mixer User' ||
            [
              'Tornado Cash Withdrawer',
              'Tornado Cash Depositor',
              'Address Funded by Mixer',
            ].some((mixer) => label.label.includes(mixer))
          ) {
            categories.MixerUser.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }

          // KYC category checks
          if (
            label.type === 'KYC' ||
            ['Withdrawer', 'Depositor'].some((kyc) => label.label.includes(kyc))
          ) {
            categories.KYC.push({
              address: address,
              chain: chain,
              label: label.label,
              type: label.type,
            });
          }
        });
      }
    });

    return categories;
  } catch (error) {
    console.error('Error categorizing nodes:', error);
    return {};
  }
};
/**
 * Extracts addresses from specified categories that should not be searched
 * with detailed information including reason for exclusion
 * @param {Object} categorizedNodes - Object with nodes categorized by type
 * @param {Array} categoriesToExclude - Array of category names to exclude from search
 * @returns {Array} - Array of objects with address, chain and reason for exclusion
 */
export const extractExcludedAddresses = (
  categorizedNodes,
  categoriesToExclude = ['Contract', 'CEX', 'DEX']
) => {
  try {
    // Initialize map to track addresses and their reasons
    const addressMap = new Map();

    // Process each category to exclude
    categoriesToExclude.forEach((category) => {
      if (
        categorizedNodes[category] &&
        Array.isArray(categorizedNodes[category])
      ) {
        categorizedNodes[category].forEach((node) => {
          // Check if node has all required fields
          if (!node || !node.address || !node.chain) {
            console.error('Invalid node found:', JSON.stringify(node));
            return;
          }

          const key = `${node.chain}:${node.address}`;

          // If address already exists in map, append the category to reasons
          if (addressMap.has(key)) {
            const existing = addressMap.get(key);
            if (!existing.reasonsForExclusion.includes(category)) {
              existing.reasonsForExclusion.push(category);
            }
          } else {
            // Add new entry
            addressMap.set(key, {
              address: node.address,
              chain: node.chain,
              reasonsForExclusion: [category],
              label: node.label || null,
              type: node.type || null,
            });
          }
        });
      }
    });

    // Convert map to array
    return Array.from(addressMap.values());
  } catch (error) {
    console.error('Error extracting excluded addresses:', error);
    return [];
  }
};

